import { c as _c } from "react/compiler-runtime";
import { Button } from '@/components/ui/Button';
import { Progress } from '@/components/ui/Progress';
import { useI18n } from '@/context/I18nProvider';
import { type OnboardingStep, useOnboardingTutorial } from '@/hooks/useOnboardingTutorial';
import { cn } from '@/lib/utils';
import { ArrowRight, Check, ChevronDown, ChevronUp, Link2, Sparkles, X, Zap } from 'lucide-react';
import { useEffect, useState } from 'react';
const STEP_CONFIG: Record<OnboardingStep, {
  icon: typeof Sparkles;
  colorClass: string;
}> = {
  connect_integration: {
    icon: Link2,
    colorClass: 'text-blue-500'
  },
  create_agent: {
    icon: Sparkles,
    colorClass: 'text-violet-500'
  },
  create_sequence: {
    icon: Zap,
    colorClass: 'text-amber-500'
  }
};
export const OnboardingChecklist = () => {
  const $ = _c(53);
  const {
    t
  } = useI18n();
  const {
    steps,
    completedSteps,
    currentStep,
    progress,
    isVisible,
    dismiss,
    goToStep
  } = useOnboardingTutorial();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [selectedStep, setSelectedStep] = useState(null);
  let t0;
  let t1;
  if ($[0] !== completedSteps || $[1] !== selectedStep) {
    t0 = () => {
      if (selectedStep && completedSteps.includes(selectedStep)) {
        setSelectedStep(null);
      }
    };
    t1 = [completedSteps, selectedStep];
    $[0] = completedSteps;
    $[1] = selectedStep;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  if (!isVisible) {
    return null;
  }
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = cn("fixed bottom-6 right-6 z-50 w-[340px]", "rounded-xl border bg-white shadow-lg", "animate-in slide-in-from-bottom-4 fade-in duration-300");
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <span className="text-lg">🚀</span>;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t) {
    t4 = t("onboardingTutorial.title");
    $[6] = t;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t4) {
    t5 = <p className="text-sm font-semibold text-text-primary truncate">{t4}</p>;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== completedSteps.length || $[11] !== steps.length || $[12] !== t) {
    t6 = t("onboardingTutorial.progress", {
      completed: completedSteps.length,
      total: steps.length
    });
    $[10] = completedSteps.length;
    $[11] = steps.length;
    $[12] = t;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t6) {
    t7 = <p className="text-xs text-muted-foreground">{t6}</p>;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t5 || $[17] !== t7) {
    t8 = <div className="flex items-center gap-2 min-w-0">{t3}<div className="min-w-0">{t5}{t7}</div></div>;
    $[16] = t5;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== isCollapsed) {
    t9 = () => setIsCollapsed(!isCollapsed);
    $[19] = isCollapsed;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== isCollapsed) {
    t10 = isCollapsed ? <ChevronUp className="size-4" /> : <ChevronDown className="size-4" />;
    $[21] = isCollapsed;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== t10 || $[24] !== t9) {
    t11 = <button type="button" onClick={t9} className="rounded-md p-1 hover:bg-muted transition-colors">{t10}</button>;
    $[23] = t10;
    $[24] = t9;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== t) {
    t12 = t("common.close");
    $[26] = t;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <X className="size-4" />;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== dismiss || $[30] !== t12) {
    t14 = <button type="button" onClick={dismiss} className="rounded-md p-1 hover:bg-muted transition-colors" aria-label={t12}>{t13}</button>;
    $[29] = dismiss;
    $[30] = t12;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== t11 || $[33] !== t14) {
    t15 = <div className="flex items-center gap-1 shrink-0">{t11}{t14}</div>;
    $[32] = t11;
    $[33] = t14;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== t15 || $[36] !== t8) {
    t16 = <div className="flex items-center justify-between px-4 py-3 border-b">{t8}{t15}</div>;
    $[35] = t15;
    $[36] = t8;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== progress) {
    t17 = <div className="px-4 pt-3"><Progress value={progress} className="h-1.5" /></div>;
    $[38] = progress;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== completedSteps || $[41] !== currentStep || $[42] !== dismiss || $[43] !== goToStep || $[44] !== isCollapsed || $[45] !== selectedStep || $[46] !== steps || $[47] !== t) {
    t18 = !isCollapsed && <div className="px-4 py-3 space-y-2">{steps.map(step => {
        const isCompleted = completedSteps.includes(step);
        const isCurrent = selectedStep ? step === selectedStep : step === currentStep;
        const config = STEP_CONFIG[step];
        const Icon = config.icon;
        return <button type="button" key={step} onClick={() => {
          if (!isCompleted) {
            setSelectedStep(step);
            goToStep(step);
          }
        }} disabled={isCompleted} className={cn("w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-all", isCompleted && "opacity-60", isCurrent && "bg-muted ring-1 ring-primary/20", !isCompleted && !isCurrent && "hover:bg-muted")}><div className={cn("flex size-8 shrink-0 items-center justify-center rounded-full", isCompleted ? "bg-green-100" : "bg-muted")}>{isCompleted ? <Check className="size-4 text-green-600" /> : <Icon className={cn("size-4", config.colorClass)} />}</div><div className="flex-1 min-w-0"><p className={cn("text-sm font-medium", isCompleted && "line-through text-muted-foreground")}>{t(`onboardingTutorial.steps.${step}.title` as never)}</p><p className="text-xs text-muted-foreground truncate">{t(`onboardingTutorial.steps.${step}.description` as never)}</p></div>{isCurrent && !isCompleted && <ArrowRight className="size-4 text-primary shrink-0" />}</button>;
      })}<div className="pt-1"><Button variant="ghost" size="sm" className="w-full text-xs" onClick={dismiss}>{t("onboardingTutorial.skip")}</Button></div></div>;
    $[40] = completedSteps;
    $[41] = currentStep;
    $[42] = dismiss;
    $[43] = goToStep;
    $[44] = isCollapsed;
    $[45] = selectedStep;
    $[46] = steps;
    $[47] = t;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  let t19;
  if ($[49] !== t16 || $[50] !== t17 || $[51] !== t18) {
    t19 = <div className={t2}>{t16}{t17}{t18}</div>;
    $[49] = t16;
    $[50] = t17;
    $[51] = t18;
    $[52] = t19;
  } else {
    t19 = $[52];
  }
  return t19;
};